import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { executeBackgroundTask } from '@ayocore/platform/core/client/background-tasks';
import logger from '@ayocore/platform/common/helpers/logger';
import type { Folder } from '../../../schemas/folder.schema';
import { resolveFileUrl } from '../../fileUrl';

/** Строковое поле из unknown-результата фоновой задачи. */
function stringField(value: unknown, key: string): string | null {
  if (typeof value !== 'object' || value === null) return null;
  const entry = Object.entries(value).find(([entryKey]) => entryKey === key);
  const field = entry?.[1];
  return typeof field === 'string' ? field : null;
}

/**
 * «Скачать папку» — ZIP поддерева Диска в фоне: ставит задачу ядра
 * 'filemanager::exportFolderArchive' (executeBackgroundTask) и ведёт одно
 * уведомление от «Собираем…» с процентами до ссылки на скачивание. Готовый
 * архив дополнительно приходит durable-уведомлением сервера — переживает
 * обрыв соединения и уход со страницы.
 */
export function useFolderArchiveDownload() {
  const [downloadingFolderId, setDownloadingFolderId] = useState<number | null>(null);

  const downloadFolderArchive = (folder: Folder) => {
    if (downloadingFolderId != null) return;
    setDownloadingFolderId(folder.id);
    const notificationId = `folder-archive-${folder.id}`;
    notifications.show({
      id: notificationId,
      loading: true,
      title: 'Собираем архив…',
      message: folder.name,
      autoClose: false,
      withCloseButton: false,
    });

    const task = executeBackgroundTask('filemanager::exportFolderArchive', {
      folderId: folder.id,
    });
    task.onProgress(({ percent }) => {
      notifications.update({
        id: notificationId,
        loading: true,
        title: 'Собираем архив…',
        message: `${folder.name} — ${percent}%`,
        autoClose: false,
        withCloseButton: false,
      });
    });
    task.onDone(result => {
      setDownloadingFolderId(null);
      const url = stringField(result, 'url');
      const filename = stringField(result, 'filename');
      notifications.update({
        id: notificationId,
        loading: false,
        color: 'green',
        title: 'Архив готов',
        message: filename ?? folder.name,
        autoClose: 6000,
        withCloseButton: true,
      });
      if (url) {
        // Попап-блокер может не пустить (не user gesture) — тогда остаётся
        // durable-уведомление сервера со ссылкой
        window.open(resolveFileUrl(url), '_blank', 'noopener');
      }
    });
    task.onError(message => {
      setDownloadingFolderId(null);
      notifications.update({
        id: notificationId,
        loading: false,
        color: 'red',
        title: 'Не удалось собрать архив',
        message,
        autoClose: 8000,
        withCloseButton: true,
      });
      logger.error('[drive] Скачивание папки не удалось:', message);
    });
  };

  return { downloadingFolderId, downloadFolderArchive };
}
